import React, { useState, useEffect } from 'react';
import { 
  UserCircleIcon, 
  ShoppingBagIcon, 
  UsersIcon, 
  BanknotesIcon,
  ArrowRightIcon,
  ArrowTopRightOnSquareIcon,
  SparklesIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  PencilIcon 
} from '@heroicons/react/24/outline';
import { Instagram, PenLine } from 'lucide-react';
import BouteeLogo from '../components/layout/Boutee_Logo_Icon.jpg';
import BouteeLogoTransparent from '../components/layout/Boutee_Logo_Transparent.png';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { calculateProfileProgress } from '../components/profile/utils/validation';
import { getJewellerRings } from '../services/product';
import { getFunctions, httpsCallable } from 'firebase/functions';
import useStripeConnectOnboarding from '../hooks/useStripeConnectOnboarding';
import StripeOnboardingNoticeModal from '../components/payments/StripeOnboardingNoticeModal';

const Dashboard = () => {
  const { jeweller, currentUser } = useAuth();
  const navigate = useNavigate();
  const [ringsCount, setRingsCount] = useState(0);
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const {
    isLoading: isStripeLoading,
    isNoticeOpen: isStripeNoticeOpen,
    openNotice: openStripeNotice,
    closeNotice: closeStripeNotice,
    continueToStripe: continueToStripeSetup
  } = useStripeConnectOnboarding();
  
  // Get time-based greeting
  const getTimeBasedGreeting = () => {
    const hour = new Date().getHours();
    
    if (hour >= 5 && hour < 12) {
      return "Good morning";
    } else if (hour >= 12 && hour < 17) {
      return "Good afternoon";
    } else if (hour >= 17 && hour < 22) {
      return "Good evening";
    } else {
      return "Good night";
    }
  };
  
  const greeting = getTimeBasedGreeting();

  const [isDashboardVideoOpen, setIsDashboardVideoOpen] = useState(false);

  useEffect(() => {
    if (!isDashboardVideoOpen) return;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsDashboardVideoOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isDashboardVideoOpen]);

  const dashboardWalkthrough = {
    title: 'What is the Jeweller Portal?',
    durationLabel: '2 mins',
    embedUrl: 'https://player.vimeo.com/video/1170341627?badge=0&autopause=0&player_id=0&app_id=58479'
  };

  // Fetch rings count on mount
  useEffect(() => {
    const fetchRingsCount = async () => {
      if (!currentUser?.uid) return;
      
      try {
        const rings = await getJewellerRings(currentUser.uid);
        setRingsCount(rings.length);
      } catch (error) {
        console.error('Error fetching rings count:', error);
        setRingsCount(0);
      }
    };

    fetchRingsCount();
  }, [currentUser]);

  const handleSubmitForReview = async () => {
    if (!currentUser?.uid) return;
    
    setIsSubmittingReview(true);
    try {
      // Call cloud function to submit for review
      const functions = getFunctions();
      const submitForReview = httpsCallable(functions, 'submitJewellerForReview');
      
      await submitForReview({
        jewellerId: currentUser.uid,
        jewellerData: {
          companyName: jeweller?.companyName || 'Unknown',
          email: jeweller?.email || '',
          firstName: jeweller?.firstName || '',
          surname: jeweller?.surname || ''
        }
      });
      
      // Refresh jeweller data to update UI
      window.location.reload();
      
    } catch (error) {
      console.error('Failed to submit for review:', error);
      alert('Failed to submit for review. Please try again.');
      setIsSubmittingReview(false);
    }
  };

  const getSetupStatus = () => {
    // Profile: Check if 100% complete using the actual validation function
    const profileProgress = calculateProfileProgress(jeweller || {});
    const isProfileComplete = profileProgress === 100;
    
    // Products: Check if user has uploaded 10 or more rings
    // Using the actual count from the rings collection
    const hasProducts = ringsCount >= 10;
    
    // Payments: Check Stripe connection status
    const stripeData = jeweller?.stripe;
    const hasStripeAccount = !!stripeData?.accountId;
    const isStripeComplete = stripeData?.status === 'connected';
    const isStripePending = stripeData?.status === 'pending';
    
    return {
      profile: isProfileComplete,
      products: hasProducts,
      payments: {
        completed: isStripeComplete,
        pending: isStripePending,
        notStarted: !hasStripeAccount
      }
    };
  };
  
  const setupStatus = getSetupStatus();
  
  // Check if jeweller is active (live on Boutee)
  const isActive = jeweller?.active === true;
  
  // Check if already submitted for review
  const isInReview = jeweller?.inReview === true;
  
  // Check if all steps are complete
  const allStepsComplete = setupStatus.profile && setupStatus.products && setupStatus.payments.completed;
  
  // Determine which step is currently active (first incomplete step)
  const getActiveStep = () => {
    if (!setupStatus.profile) return 1;
    if (!setupStatus.products) return 2;
    if (!setupStatus.payments.completed) return 3;
    return null; // All complete
  };
  
  const activeStep = getActiveStep();


  // Quick Link Component
  const QuickLink = ({ to, icon, gradientVar, title, description }) => (
    <Link to={to}>
      <div 
        className="h-full relative overflow-hidden group cursor-pointer"
        style={{
          borderRadius: 'var(--radius-m)',
          border: '1px solid var(--Stroke-Light)',
          backgroundColor: 'var(--Surface-Primary)',
          boxShadow: 'var(--shadow-frame-small)',
          padding: 'var(--spacing-l)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-4px)';
          e.currentTarget.style.boxShadow = 'var(--shadow-frame-big)';
          e.currentTarget.style.borderColor = 'var(--Purple-300)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'var(--shadow-frame-small)';
          e.currentTarget.style.borderColor = 'var(--Stroke-Light)';
        }}
      >
        {/* Decorative gradient accent */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: gradientVar
        }} />
        
        <div className="flex flex-col h-full">
          {/* Icon container */}
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: 'var(--radius-s)',
            background: gradientVar,
            padding: '10px',
            marginBottom: 'var(--spacing-m)',
            transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
          className="group-hover:scale-110"
          >
            {React.cloneElement(icon, { 
              className: 'w-full h-full',
              style: { color: 'var(--Text-Contrast)' }
            })}
          </div>
          
          {/* Content */}
          <h3 style={{
            fontSize: 'var(--font-size-subtitle2)',
            fontWeight: 'var(--font-weight-subtitle2)',
            color: 'var(--Text-Primary)',
            marginBottom: 'var(--spacing-xs)'
          }}>
            {title}
          </h3>
          <p style={{
            fontSize: 'var(--font-size-body4)',
            color: 'var(--Text-Secondary)',
            marginBottom: 'var(--spacing-m)',
            flexGrow: 1
          }}>
            {description}
          </p>
          
          {/* Action */}
          <div className="flex items-center justify-between">
            <span 
              className="flex items-center transition-colors"
              style={{
                fontSize: 'var(--font-size-body4)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--Purple-600)',
                gap: 'var(--spacing-xs)'
              }}
            >
              Go to {title}
              <ArrowRightIcon 
                className="w-4 h-4 transition-transform group-hover:translate-x-1"
              />
            </span>
          </div>
        </div>
      </div>
    </Link>
  );

  // TEMPORARY: Set to false to disable payments setup
  const PAYMENTS_ENABLED = true;

  // Setup Step Component
  const SetupStep = ({ stepNumber, title, description, buttonText, link, onClick, isLoading, isActive, isCompleted, isDisabled }) => (
    <div className="flex-1 relative" style={{ zIndex: 1 }}>
      <div style={{
        backgroundColor: 'var(--Surface-Primary)',
        borderRadius: 'var(--radius-m)',
        padding: 'var(--spacing-l)',
        border: `2px solid ${isActive ? 'var(--Purple-500)' : isCompleted ? 'var(--Green-500)' : 'var(--Grey-300)'}`,
        textAlign: 'center',
        transition: 'all 0.3s ease',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}>
        {/* Step Number/Check */}
        <div className="flex justify-center" style={{ marginBottom: 'var(--spacing-m)' }}>
          {isCompleted ? (
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, var(--Green-500) 0%, var(--Green-600) 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: 'var(--shadow-md)'
            }}>
              <CheckCircleIcon style={{ 
                width: '20px', 
                height: '20px',
                color: 'var(--Text-Contrast)' 
              }} />
            </div>
          ) : (
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              background: isActive && !isDisabled ? 'var(--brand-gradient-1)' : 'var(--Grey-100)',
              border: isActive && !isDisabled ? 'none' : '2px solid var(--Grey-300)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: isActive && !isDisabled ? 'var(--Text-Contrast)' : 'var(--Text-Secondary)',
              fontSize: 'var(--font-size-body2)',
              fontWeight: 'var(--font-weight-bold)',
              boxShadow: isActive && !isDisabled ? 'var(--shadow-md)' : 'none'
            }}>
              {stepNumber}
            </div>
          )}
        </div>
        
        <h4 style={{
          fontSize: 'var(--font-size-subtitle2)',
          fontWeight: 'var(--font-weight-subtitle2)',
          color: isDisabled ? 'var(--Grey-500)' : 'var(--Text-Primary)',
          marginBottom: 'var(--spacing-xs)'
        }}>
          {title}
        </h4>
        <p style={{
          fontSize: 'var(--font-size-body4)',
          color: isDisabled ? 'var(--Grey-400)' : 'var(--Text-Secondary)',
          marginBottom: 'var(--spacing-m)',
          flexGrow: 1
        }}>
          {description}
        </p>
        
        {!isCompleted && (
          <button 
            onClick={onClick ? onClick : () => navigate(link)}
            disabled={isLoading || isDisabled}
            style={{
              width: '100%',
              padding: 'var(--spacing-s) var(--spacing-m)',
              background: isActive && !isDisabled ? 'var(--brand-gradient-1)' : 'transparent',
              backgroundColor: !isActive || isDisabled ? 'var(--Surface-Secondary)' : undefined,
              color: isActive && !isDisabled ? 'var(--Text-Contrast)' : 'var(--Text-Primary)',
              border: isActive && !isDisabled ? 'none' : '1px solid var(--Stroke-Secondary)',
              borderRadius: 'var(--button-radius)',
              fontSize: 'var(--font-size-body3)',
              fontWeight: 'var(--font-weight-medium)',
              transition: 'all 0.2s ease',
              opacity: isLoading || isDisabled ? 0.6 : 1,
              cursor: isLoading || isDisabled ? 'not-allowed' : 'pointer'
            }}
            onMouseEnter={(e) => {
              if (isActive && !isLoading && !isDisabled) {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = 'var(--shadow-primary)';
              } else if (!isLoading && !isDisabled) {
                e.target.style.backgroundColor = 'var(--Grey-100)';
                e.target.style.borderColor = 'var(--Purple-400)';
              }
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = 'none';
              if (!isActive || isDisabled) {
                e.target.style.backgroundColor = 'var(--Surface-Secondary)';
                e.target.style.borderColor = 'var(--Stroke-Secondary)';
              }
            }}
          >
            {isLoading ? 'Connecting...' : buttonText}
          </button>
        )}
        
        {isCompleted && (
          <div style={{
            padding: 'var(--spacing-s)',
            backgroundColor: 'var(--Green-50)',
            color: 'var(--Green-700)',
            borderRadius: 'var(--radius-xs)',
            fontSize: 'var(--font-size-body4)',
            fontWeight: 'var(--font-weight-medium)'
          }}>
            Completed ✓
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="animate-fadeIn">
      {/* Welcome Section */}
      <div style={{ marginBottom: 'var(--spacing-xxl)' }}>
        <div 
          className="relative overflow-hidden"
          style={{
            background: 'var(--brand-gradient-1)',
            borderRadius: 'var(--radius-xl)',
            padding: 'var(--spacing-xxxl)',
            marginBottom: 'var(--spacing-xl)',
            boxShadow: 'var(--shadow-frame-big)'
          }}
        >
          {/* Background decoration */}
          <div 
            className="absolute top-0 right-0 opacity-15"
            style={{ 
              width: '290px', 
              height: '290px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <img 
              src={BouteeLogoTransparent}
              alt="Boutee" 
              style={{ 
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                filter: 'brightness(0) invert(1)'
              }} 
            />
          </div>
          
          <div className="relative z-10">
            <h1 style={{
              fontSize: 'var(--font-size-h3)',
              fontWeight: 'var(--font-weight-h2)',
              color: 'var(--Text-Contrast)',
              marginBottom: 'var(--spacing-xs)'
            }}>
              {greeting}, {jeweller?.firstName || 'Jeweller'}
            </h1>
            <p style={{
              color: 'var(--Text-Contrast)',
              opacity: 0.9,
              fontSize: 'var(--font-size-body2)',
              lineHeight: 'var(--line-height-body2)'
            }}>
              {isActive 
                ? 'Your profile is live on Boutee! Manage your jeweller profile, products, and more from your dashboard'
                : 'Complete your setup to go live on Boutee and start receiving enquiries'}
            </p>
            <div style={{ marginTop: 'var(--spacing-s)', display: 'inline-flex', alignItems: 'center', gap: 'var(--spacing-xs)' }}>
              <button
                type="button"
                onClick={() => setIsDashboardVideoOpen(true)}
                style={{
                  background: 'rgba(255, 255, 255, 0.18)',
                  border: '1px solid rgba(255, 255, 255, 0.35)',
                  padding: '6px 12px',
                  borderRadius: '999px',
                  color: '#fff',
                  fontSize: 'var(--font-size-body4)',
                  fontWeight: 'var(--font-weight-medium)',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 'var(--spacing-xxs)',
                  backdropFilter: 'blur(6px)'
                }}
              >
                <span style={{ fontSize: '12px' }}>▶</span>
                What is the Jeweller Portal?
              </button>
              {dashboardWalkthrough.durationLabel && (
                <span style={{ fontSize: 'var(--font-size-body5)', color: 'rgba(255, 255, 255, 0.85)' }}>
                  {dashboardWalkthrough.durationLabel}
                </span>
              )}
            </div>
          </div>
        </div>
        
        <StripeOnboardingNoticeModal
          isOpen={isStripeNoticeOpen}
          onClose={closeStripeNotice}
          onContinue={continueToStripeSetup}
          isSubmitting={isStripeLoading}
        />

        {isDashboardVideoOpen && (
          <div
            onClick={() => setIsDashboardVideoOpen(false)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(9, 11, 15, 0.55)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: 'var(--spacing-l)'
            }}
          >
            <div
              onClick={(event) => event.stopPropagation()}
              style={{
                width: 'min(1100px, 92vw)',
                maxHeight: '85vh',
                background: 'var(--Surface-Primary)',
                borderRadius: 'var(--radius-xl)',
                boxShadow: 'var(--shadow-lg)',
                border: 'var(--card-border)',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: 'var(--spacing-l)',
                borderBottom: 'var(--card-border)'
              }}>
                <div style={{ fontSize: 'var(--font-size-body2)', fontWeight: 'var(--font-weight-medium)', color: 'var(--Text-Primary)' }}>
                  {dashboardWalkthrough.title}
                </div>
                <button
                  type="button"
                  onClick={() => setIsDashboardVideoOpen(false)}
                  style={{ background: 'transparent', border: 'none', color: 'var(--Text-Secondary)', cursor: 'pointer', fontSize: '1.2rem' }}
                  aria-label="Close video"
                >
                  ✕
                </button>
              </div>

              <div style={{ position: 'relative', aspectRatio: '16 / 9', width: '100%', flex: 1, minHeight: 0, background: 'var(--Grey-900)' }}>
                <iframe
                  src={dashboardWalkthrough.embedUrl}
                  title={dashboardWalkthrough.title}
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Three-Step Setup Section - Only show if NOT active */}
        {!isActive && (
          <div style={{
            backgroundColor: 'var(--Surface-Primary)',
            borderRadius: 'var(--radius-xl)',
            padding: 'var(--spacing-xxl)',
            border: '1px solid var(--Stroke-Light)',
            boxShadow: 'var(--shadow-frame-small)',
            marginBottom: 'var(--spacing-xxl)'
          }}>
            <h2 style={{
              fontSize: 'var(--font-size-h4)',
              fontWeight: 'var(--font-weight-h4)',
              color: 'var(--Text-Primary)',
              marginBottom: 'var(--spacing-xl)',
              textAlign: 'center'
            }}>
              Complete Your Setup
            </h2>
            
            <div 
              className="flex flex-col md:flex-row items-stretch"
              style={{ gap: 'var(--spacing-m)', position: 'relative' }}
            >
              {/* Progress Line - Desktop Only */}
              <div 
                className="hidden md:block absolute"
                style={{ 
                  top: '32px',
                  left: '20%',
                  right: '20%',
                  height: '2px',
                  background: setupStatus.profile 
                    ? (setupStatus.products 
                      ? 'linear-gradient(to right, var(--Green-400) 0%, var(--Green-400) 66%, var(--Grey-200) 66%, var(--Grey-200) 100%)'
                      : 'linear-gradient(to right, var(--Green-400) 0%, var(--Green-400) 33%, var(--Grey-200) 33%, var(--Grey-200) 100%)')
                    : 'var(--Grey-200)',
                  zIndex: 0
                }}
              />
              
              <SetupStep
                stepNumber={1}
                title="Complete Profile"
                description="Add all required business details to reach 100% completion"
                buttonText="Setup Profile"
                link="/profile"
                isActive={activeStep === 1}
                isCompleted={setupStatus.profile}
              />
              
              <SetupStep
                stepNumber={2}
                title="Add Products"
                description="Upload at least 10 rings to your portfolio"
                buttonText="Add Products"
                link="/products"
                isActive={activeStep === 2}
                isCompleted={setupStatus.products}
              />
              
              <SetupStep
                stepNumber={3}
                title="Setup Payments"
                description={PAYMENTS_ENABLED 
                  ? "Connect with Stripe to send invoices and recieve payments" 
                  : "Coming soon - Payment setup temporarily unavailable"}
                buttonText={PAYMENTS_ENABLED 
                  ? (setupStatus.payments.pending ? "Continue Setup" : "Setup Payments")
                  : "Start Setup"}
                onClick={PAYMENTS_ENABLED ? openStripeNotice : null}
                isLoading={PAYMENTS_ENABLED ? isStripeLoading : false}
                isActive={PAYMENTS_ENABLED ? (activeStep === 3) : false}
                isCompleted={setupStatus.payments.completed}
                isDisabled={!PAYMENTS_ENABLED}
              />
            </div>

            {/* Submit for Review Button - Shows when all steps complete */}
            {allStepsComplete && (
              <div style={{
                marginTop: 'var(--spacing-xl)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 'var(--spacing-m)'
              }}>
                <div style={{
                  width: '100%',
                  height: '2px',
                  background: 'var(--Grey-200)',
                  marginBottom: 'var(--spacing-m)'
                }} />
                
                <div style={{
                  textAlign: 'center',
                  marginBottom: 'var(--spacing-m)'
                }}>
                  <h3 style={{
                    fontSize: 'var(--font-size-subtitle1)',
                    fontWeight: 'var(--font-weight-subtitle1)',
                    color: 'var(--Text-Primary)',
                    marginBottom: 'var(--spacing-xs)'
                  }}>
                    {isInReview ? 'Review Pending' : 'Ready to Go Live?'}
                  </h3>
                  {isInReview && (
                    <div style={{
                      marginBottom: 'var(--spacing-m)',
                      padding: 'var(--spacing-m)',
                      borderRadius: 'var(--radius-l)',
                      background: 'linear-gradient(135deg, var(--Purple-50) 0%, var(--Blue-50) 100%)',
                      border: '1px solid var(--Purple-200)',
                      boxShadow: 'var(--shadow-frame-small)',
                      textAlign: 'left'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: 'var(--spacing-m)',
                        flexWrap: 'wrap'
                      }}>
                        <div style={{ flex: '1 1 260px' }}>
                          <p style={{
                            fontSize: 'var(--font-size-body3)',
                            fontWeight: 'var(--font-weight-semibold)',
                            color: 'var(--Text-Primary)',
                            margin: 0,
                            marginBottom: 'var(--spacing-xs)'
                          }}>
                            Help us improve onboarding
                          </p>
                          <p style={{
                            fontSize: 'var(--font-size-body4)',
                            color: 'var(--Text-Secondary)',
                            margin: 0,
                            lineHeight: '1.5'
                          }}>
                            Your profile is now in review. While it&apos;s being checked, please share a few quick thoughts on your onboarding experience.
                          </p>
                        </div>

                        <a
                          href="https://docs.google.com/forms/d/e/1FAIpQLScdgpzCOELBt3V0iUhFnnf8GftQvHGxftD8Yhy6bpwX2fgPLw/viewform?usp=header"
                          target="_blank"
                          rel="noreferrer"
                          style={{ textDecoration: 'none' }}
                        >
                          <button
                            type="button"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 'var(--spacing-xs)',
                              padding: 'var(--spacing-s) var(--spacing-m)',
                              borderRadius: 'var(--button-radius)',
                              background: 'var(--brand-gradient-1)',
                              color: 'var(--Text-Contrast)',
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: 'var(--font-size-body4)',
                              fontWeight: 'var(--font-weight-semibold)',
                              boxShadow: 'var(--shadow-primary)',
                              whiteSpace: 'nowrap',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'translateY(-2px)';
                              e.currentTarget.style.boxShadow = 'var(--shadow-popup)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = 'var(--shadow-primary)';
                            }}
                          >
                            <span>Share Feedback</span>
                            <ArrowTopRightOnSquareIcon style={{ width: '16px', height: '16px' }} />
                          </button>
                        </a>
                      </div>
                    </div>
                  )}
                  <p style={{
                    fontSize: 'var(--font-size-body4)',
                    color: 'var(--Text-Secondary)'
                  }}>
                    {isInReview 
                      ? 'Your profile has been submitted and is under review. We\'ll email you once approved!' 
                      : 'All setup steps complete! Submit your profile for review to go live on Boutee.'}
                  </p>
                </div>

                <button
                  onClick={handleSubmitForReview}
                  disabled={isInReview || isSubmittingReview}
                  style={{
                    padding: 'var(--spacing-m) var(--spacing-xxl)',
                    background: isInReview ? 'var(--Grey-300)' : 'var(--brand-gradient-1)',
                    color: isInReview ? 'var(--Text-Secondary)' : 'var(--Text-Contrast)',
                    border: 'none',
                    borderRadius: 'var(--button-radius)',
                    fontSize: 'var(--font-size-body2)',
                    fontWeight: 'var(--font-weight-semibold)',
                    cursor: isInReview || isSubmittingReview ? 'not-allowed' : 'pointer',
                    boxShadow: isInReview ? 'none' : 'var(--shadow-primary)',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-s)',
                    opacity: isInReview || isSubmittingReview ? 0.7 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!isInReview && !isSubmittingReview) {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = 'var(--shadow-popup)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    if (!isInReview) {
                      e.target.style.boxShadow = 'var(--shadow-primary)';
                    }
                  }}
                >
                  {isSubmittingReview ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Submitting...
                    </>
                  ) : isInReview ? (
                    <>
                      <CheckCircleIcon className="w-5 h-5" />
                      Profile Submitted for Review
                    </>
                  ) : (
                    <>
                      <SparklesIcon className="w-5 h-5" />
                      Submit for Review
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div 
        className="grid grid-cols-1 md:grid-cols-2"
        style={{ gap: 'var(--spacing-m)', marginBottom: 'var(--spacing-l)' }}
      >
        {/* Submit Content Box */}
        <div style={{
          backgroundColor: 'var(--Surface-Primary)',
          borderRadius: 'var(--radius-l)',
          padding: 'var(--spacing-l)',
          border: '1px solid var(--Stroke-Light)',
          boxShadow: 'var(--shadow-frame-small)'
        }}>
          <div>
            <h3 style={{
              fontSize: 'var(--font-size-subtitle2)',
              fontWeight: 'var(--font-weight-subtitle2)',
              color: 'var(--Text-Primary)',
              marginBottom: 'var(--spacing-xs)',
              margin: 0
            }}>
              Submit Content
            </h3>
            <p style={{
              fontSize: 'var(--font-size-body4)',
              color: 'var(--Text-Secondary)',
              margin: 0,
              marginBottom: 'var(--spacing-m)',
              lineHeight: '1.5'
            }}>
              Share your latest photos and videos of your jewellery, workshop, and any news you’d like to highlight. We’ll post it on our socials to get more eyes on your content.
            </p>
            
            <Link to="/social-media">
              <button
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 'var(--spacing-xs)',
                  padding: 'var(--spacing-xs) var(--spacing-s)',
                  borderRadius: 'var(--radius-full)',
                  color: 'var(--Text-Primary)',
                  backgroundColor: 'var(--Surface-Secondary)',
                  border: '1px solid var(--Stroke-Light)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  fontSize: 'var(--font-size-body4)',
                  fontWeight: 'var(--font-weight-medium)',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--Purple-50)';
                  e.currentTarget.style.borderColor = 'var(--Purple-200)';
                  e.currentTarget.style.color = 'var(--Purple-700)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--Surface-Secondary)';
                  e.currentTarget.style.borderColor = 'var(--Stroke-Light)';
                  e.currentTarget.style.color = 'var(--Text-Primary)';
                }}
              >
                <Instagram size={16} />
                <span>Submit</span>
              </button>
            </Link>
          </div>
        </div>

        {/* Publish Blog Box */}
        <div style={{
          backgroundColor: 'var(--Surface-Primary)',
          borderRadius: 'var(--radius-l)',
          padding: 'var(--spacing-l)',
          border: '1px solid var(--Stroke-Light)',
          boxShadow: 'var(--shadow-frame-small)'
        }}>
          <div>
            <h3 style={{
              fontSize: 'var(--font-size-subtitle2)',
              fontWeight: 'var(--font-weight-subtitle2)',
              color: 'var(--Text-Primary)',
              marginBottom: 'var(--spacing-xs)',
              margin: 0
            }}>
              Feature Your Blog
            </h3>
            <p style={{
              fontSize: 'var(--font-size-body4)',
              color: 'var(--Text-Secondary)',
              margin: 0,
              marginBottom: 'var(--spacing-m)',
              lineHeight: '1.5'
            }}>
              Want your blog featured on Boutee? We’ll spotlight it on our blog page and share it with our community. Make the most out of your blogs!
            </p>
            <a
              href="https://forms.gle/Dym7Qifu2GrbjkxD6"
              target="_blank"
              rel="noreferrer"
            >
              <button
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 'var(--spacing-xs)',
                  padding: 'var(--spacing-xs) var(--spacing-s)',
                  borderRadius: 'var(--radius-full)',
                  color: 'var(--Text-Primary)',
                  backgroundColor: 'var(--Surface-Secondary)',
                  border: '1px solid var(--Stroke-Light)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  fontSize: 'var(--font-size-body4)',
                  fontWeight: 'var(--font-weight-medium)',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--Purple-50)';
                  e.currentTarget.style.borderColor = 'var(--Purple-200)';
                  e.currentTarget.style.color = 'var(--Purple-700)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--Surface-Secondary)';
                  e.currentTarget.style.borderColor = 'var(--Stroke-Light)';
                  e.currentTarget.style.color = 'var(--Text-Primary)';
                }}
              >
                <PenLine size={16} />
                <span>Submit</span>
              </button>
            </a>
          </div>
        </div>
      </div>

      <div style={{
        backgroundColor: 'var(--Surface-Primary)',
        borderRadius: 'var(--radius-xl)',
        padding: 'var(--spacing-xxl)',
        border: '1px solid var(--Stroke-Light)',
        boxShadow: 'var(--shadow-frame-small)',
        marginBottom: 'var(--spacing-xxl)'
      }}>
        {/* Icon and Title */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 'var(--spacing-m)',
          marginBottom: 'var(--spacing-l)'
        }}>
          {/* Info Icon with gradient background */}
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: 'var(--radius-m)',
            background: 'linear-gradient(135deg, var(--Red-500) 0%, var(--Red-600) 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <ExclamationTriangleIcon style={{ width: '24px', height: '24px', color: 'white' }} />
          </div>
          
          <h2 style={{
            fontSize: 'var(--font-size-h5)',
            fontWeight: 'var(--font-weight-h5)',
            color: 'var(--Text-Primary)',
            margin: 0
          }}>
            Important Notifications
          </h2>
        </div>

        {/* Notification Content */}
        <div style={{
          backgroundColor: 'var(--Blue-50)',
          borderRadius: 'var(--radius-m)',
          padding: 'var(--spacing-l)',
          border: '1px solid var(--Blue-200)'
        }}>
          <p style={{
            fontSize: 'var(--font-size-body3)',
            color: 'var(--Text-Primary)',
            margin: 0,
            lineHeight: '1.6'
          }}>
            <strong>Required:</strong> Add an introduction video to help customers connect with your brand.{' '}
            <Link to="/documents" style={{ color: 'var(--Blue-600)', textDecoration: 'underline' }}>
              Learn how
            </Link>
          </p>
        </div>

        <div style={{
          backgroundColor: 'var(--Blue-50)',
          borderRadius: 'var(--radius-m)',
          padding: 'var(--spacing-l)',
          border: '1px solid var(--Blue-200)',
          marginTop: 'var(--spacing-m)'
        }}>
          <p style={{
            fontSize: 'var(--font-size-body3)',
            color: 'var(--Text-Primary)',
            margin: 0,
            lineHeight: '1.6'
          }}>
            <strong>Portal Checklist:</strong> Make sure your profile is ready to go live on Boutee{' '}
            <Link to="/documents" style={{ color: 'var(--Blue-600)', textDecoration: 'underline' }}>
            See more
            </Link>
          </p>
        </div>
      </div>

      {/* Quick Actions Grid */}
      <div style={{ marginBottom: 'var(--spacing-xl)' }}>
        <h2 style={{
          fontSize: 'var(--font-size-h5)',
          fontWeight: 'var(--font-weight-h5)',
          color: 'var(--Text-Primary)',
          marginBottom: 'var(--spacing-l)'
        }}>
          Quick Actions
        </h2>
      </div>
      
      <div 
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"
        style={{ gap: 'var(--spacing-m)' }}
      >
        <QuickLink
          to="/profile"
          icon={<UserCircleIcon />}
          gradientVar="linear-gradient(135deg, var(--Blue-500) 0%, var(--Blue-600) 100%)"
          title="Profile"
          description="Update your business information and profile settings"
        />
        
        <QuickLink
          to="/products"
          icon={<ShoppingBagIcon />}
          gradientVar="linear-gradient(135deg, var(--Amber-500) 0%, var(--Amber-600) 100%)"
          title="Products"
          description="Manage your product catalog"
        />
        
        <QuickLink
          to="/enquiries"
          icon={<UsersIcon />}
          gradientVar="linear-gradient(135deg, var(--Red-500) 0%, var(--Red-600) 100%)"
          title="Enquiries"
          description="Track customer relationships and interactions"
        />
        
        <QuickLink
          to="/invoices"
          icon={<BanknotesIcon />}
          gradientVar="linear-gradient(135deg, var(--Green-500) 0%, var(--Green-600) 100%)"
          title="Invoices"
          description="Create and manage customer invoices"
        />
      </div>
    </div>
  );
};

export default Dashboard;
